<template>
	<v-app :aria-busy="working ? 'true' : undefined">
		<v-progress-linear v-if="working" indeterminate color="primary" height="3" class="bed-working" aria-label="Working…" />
		<v-app-bar v-if="!onLogin" density="compact" color="surface" flat>
			<v-app-bar-title class="text-truncate">
				<img src="/favicon.svg" alt="" class="bed-logo mr-2" />Bed
			</v-app-bar-title>
			<template v-slot:append>
				<v-chip v-if="status.data?.usingMocks" color="warning" size="x-small" class="mr-1">MOCK</v-chip>
				<v-chip :color="modeColor('left')" size="x-small" class="mr-1">{{ chipLabel("left") }}</v-chip>
				<v-chip :color="modeColor('right')" size="x-small" class="mr-2">{{ chipLabel("right") }}</v-chip>
			</template>
		</v-app-bar>

		<v-main>
			<router-view />
		</v-main>

		<v-bottom-navigation v-if="!onLogin" :model-value="$route.name" grow class="bed-bottom-nav">
			<v-btn value="dashboard" to="/">
				<v-icon>mdi-view-dashboard</v-icon>
				<span>Dashboard</span>
			</v-btn>
			<v-btn value="schedule" to="/schedule">
				<v-icon>mdi-clock-outline</v-icon>
				<span>Schedule</span>
			</v-btn>
			<v-btn value="history" to="/history">
				<v-icon>mdi-chart-line</v-icon>
				<span>History</span>
			</v-btn>
			<v-btn value="settings" to="/settings">
				<v-icon>mdi-cog</v-icon>
				<span>Settings</span>
			</v-btn>
		</v-bottom-navigation>
	</v-app>
</template>

<script setup>
import { computed, onUnmounted, watch } from "vue";
import { useRoute } from "vue-router";
import { useDisplay, useTheme } from "vuetify";
import statusStore from "./stores/statusStore.js";
import { working } from "./api.js";
import { useAllSideNames } from "./composables/useSideName.js";
import { useSelectedSide } from "./composables/useSelectedSide.js";

var status = statusStore.data;
var names = useAllSideNames();
var display = useDisplay();

// Tint the whole app's primary color by which side is selected.
// Left = blue (the original theme), Right = pink (matches the History chart's
// right-side line + the bed icon in SideSelector).
var theme = useTheme();
var selectedSide = useSelectedSide();
var SIDE_PRIMARY = { left: "#1e88e5", right: "#ec407a" };
watch(selectedSide, (s) => {
	var color = SIDE_PRIMARY[s] ?? SIDE_PRIMARY.left;
	theme.themes.value.dark.colors.primary = color;
}, { immediate: true });

var anyMock = computed(() => {
	var m = status.data?.useMocks ?? {};
	return !!(m.sleepme || m.smartthings || m.sleepiq);
});

var mockTitle = computed(() => {
	var m = status.data?.useMocks ?? {};
	var on = [];
	if (m.sleepme) { on.push("SleepMe"); }
	if (m.smartthings) { on.push("SmartThings"); }
	if (m.sleepiq) { on.push("SleepIQ"); }
	return "Mock data for: " + on.join(", ");
});

function sideMode(side) {
	return status.data?.sides?.[side]?.mode ?? "—";
}

function modeColor(side) {
	switch (status.data?.sides?.[side]?.mode) {
	case "auto": return "success";
	case "manual": return "warning";
	case "off": return "grey";
	default: return undefined;
	}
}

function shortName(name) {
	if (!name) { return "—"; }
	return name.split(/[\s']/)[0];
}

function chipLabel(side) {
	var mode = sideMode(side);
	if (display.xs.value) {
		var initial = (names.value[side] ?? side).slice(0, 1).toUpperCase();
		return initial + ":" + mode.charAt(0).toUpperCase();
	}
	return shortName(names.value[side]) + ": " + mode;
}

// Poll only when authenticated: on the login page the token is missing or
// rejected, so every poll would 401 (and re-trigger api.js's login redirect).
var route = useRoute();
var onLogin = computed(() => route.name === "login");
watch(onLogin, (v) => {
	if (v) { statusStore.methods.stopPolling(); }
	else { statusStore.methods.startPolling(10000); }
}, { immediate: true });
onUnmounted(() => statusStore.methods.stopPolling());
</script>

<style>
/* Home-screen (standalone) iPhone: the page runs under the status bar and
 * home indicator. Grow both bars by the inset, and push v-main by the same
 * amount, since Vuetify's layout only knows the bars' nominal heights.
 * The bottom nav's height is an inline style, hence !important. */
.v-application .v-app-bar {
	padding-top: env(safe-area-inset-top);
}
.v-application .bed-bottom-nav {
	height: calc(56px + env(safe-area-inset-bottom)) !important;
	padding-bottom: env(safe-area-inset-bottom);
}
.v-application .bed-working {
	position: fixed;
	top: env(safe-area-inset-top);
	left: 0;
	right: 0;
	z-index: 10000;
}
.bed-logo {
	width: 26px;
	height: 26px;
	border-radius: 22.5%;
	vertical-align: middle;
}
.v-application .v-main {
	padding-top: calc(var(--v-layout-top) + env(safe-area-inset-top));
	padding-bottom: calc(var(--v-layout-bottom) + env(safe-area-inset-bottom));
}
/* Home-screen app: no double-tap zoom, so quick repeated taps register as
 * taps. touch-action on the root covers every descendant. Pinch zoom
 * still works. */
@media (display-mode: standalone) {
	html {
		touch-action: manipulation;
	}
}
</style>
